<script setup lang="ts">
import { computed, type PropType } from "vue";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import ImageFieldEditor from "@/components/Reports/ReportBuilderV2/ImageFieldEditor.vue";
import { IMAGE_SIZE_MAX, IMAGE_SIZE_MIN, imageSizePercent } from "./constants";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";
import type { ResultsCard } from "@/types";

interface ImageBinding {
  value?: string | null;
  photographer?: string | null;
  photographer_url?: string | null;
}

const props = defineProps({
  card: { type: Object as PropType<ResultsCard>, required: true },
});

const cardsStore = useResultsV2CardsStore();

// ImageFieldEditor speaks the report-slide image binding shape: { value, photographer, photographer_url }.
const imageBinding = computed<ImageBinding>(() => ({
  value: (props.card.content?.url as string | undefined) || null,
  photographer: (props.card.content?.photographer as string | undefined) || null,
  photographer_url: (props.card.content?.photographer_url as string | undefined) || null,
}));

function setImage(image: ImageBinding) {
  cardsStore.updateCardLocal(props.card.id, {
    content: {
      ...props.card.content,
      url: image.value || "",
      photographer: image.photographer || null,
      photographer_url: image.photographer_url || null,
    },
  });
}

const caption = computed({
  get: () => String(props.card.content?.caption || ""),
  set: (val: string) => {
    cardsStore.updateCardLocal(props.card.id, {
      content: { ...props.card.content, caption: val },
    });
  },
});

const sizePercent = computed(() => imageSizePercent(props.card));

function setSizePercent([value]: number[]) {
  if (value === undefined || value === sizePercent.value) return;
  const { width: _width, ...options } = props.card.options || {};
  cardsStore.updateCardLocal(props.card.id, {
    options: { ...options, size_percent: value },
  });
}
</script>

<template>
  <OptionSection
    id="display"
    title="Display">
    <OptionRow
      label="Image"
      hint="The picture shown in the card.">
      <ImageFieldEditor :binding="imageBinding" @update:image="setImage" />
    </OptionRow>
    <OptionRow
      label="Caption"
      hint="A short caption under the picture.">
      <Input
        :model-value="caption"
        placeholder="Optional caption..."
        @update:model-value="caption = String($event)" />
    </OptionRow>
    <OptionRow
      label="Size"
      hint="Width of the picture in the card.">
      <div class="flex items-center gap-3">
        <Slider
          :model-value="[sizePercent]"
          :min="IMAGE_SIZE_MIN"
          :max="IMAGE_SIZE_MAX"
          :step="5"
          class="flex-1"
          @update:model-value="setSizePercent($event || [])" />
        <span class="w-10 shrink-0 text-right text-13 tabular-nums text-muted-foreground">{{ sizePercent }}%</span>
      </div>
    </OptionRow>
  </OptionSection>
</template>
